
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import './FutbolistaCard.css';

const FutbolistaCard = ({
  futbolista,
  calcularEdad,
  getTemporadaActual,
  temporadaActivaSistema,
  onRenovar
}) => {
  const navigate = useNavigate();

  const handleCardClick = () => {
    navigate(`/tutor/futbolistas/${futbolista.id}/propoints`);
  };
  const temporadaActual = getTemporadaActual(futbolista);

  // Necesita renovación si no tiene ficha de la temporada activa, o si la tiene pero
  // incompleta: comprar antes de renovar crea la ficha sin demarcación, y sin este
  // segundo caso el botón desaparecía y el tutor se quedaba sin forma de completarla.
  const fichaTemporadaActiva = temporadaActivaSistema && futbolista.temporadas
    ? futbolista.temporadas.find(t => t.temporada?.id === temporadaActivaSistema.id)
    : null;
  const necesitaRenovar = Boolean(
    temporadaActivaSistema &&
    futbolista.temporadas &&
    (!fichaTemporadaActiva || !fichaTemporadaActiva.posicion_1)
  );
  const edad = calcularEdad(futbolista.fecha_nacimiento);

  // Determinar color por edad
  const getColorByAge = (edad) => {
    if (edad >= 7 && edad <= 8) return 'age-7-8'; // 7-8 años
    if (edad >= 9 && edad <= 10) return 'age-9-10'; // 9-10 años
    if (edad >= 11 && edad <= 12) return 'age-11-12'; // 11-12 años
    if (edad >= 13 && edad <= 14) return 'age-13-14'; // 13-14 años
    return 'age-default'; // Fuera de rango - color neutro
  };

  // Determinar imagen de fondo según posición y género
  const getBackgroundImage = (posicion, genero) => {
    // Validar que posicion existe
    if (!posicion) return null;

    // Convertir a string si no lo es (puede ser un objeto o número)
    let posicionStr = '';
    if (typeof posicion === 'string') {
      posicionStr = posicion;
    } else if (typeof posicion === 'object' && posicion.posicion) {
      // Si es un objeto con propiedad posicion
      posicionStr = posicion.posicion;
    } else if (typeof posicion === 'number') {
      // Si es un ID numérico, mapear a string
      const posiciones = {
        1: 'Portero',
        2: 'Defensa central/Lateral',
        3: 'Medio/Extremo',
        4: 'Delantero/Mediapunta'
      };
      posicionStr = posiciones[posicion] || 'Jugador de campo';
    } else {
      // Intentar convertir a string
      posicionStr = String(posicion);
    }

    // Verificar si es portero (case insensitive)
    const esPortero = posicionStr.toLowerCase().includes('portero');

    // Verificar si es femenino
    const esFemenino = genero === 'F';

    // Detectar resolución de pantalla para usar 1x o 4x
    const isHighDPI = window.devicePixelRatio > 1.5;
    const folder = isHighDPI ? '4x' : '1x';

    // Seleccionar imagen según género y posición
    let imagen;
    if (esFemenino) {
      imagen = esPortero ? 'la_portero.png' : 'la_jugadorcampo.png';
    } else {
      imagen = esPortero ? 'portero.jpeg' : 'jugadorcampo.jpeg';
    }

    return `/img/${folder}/${imagen}`;
  };

  const ageColorClass = getColorByAge(edad);
  const backgroundImage = getBackgroundImage(temporadaActual?.posicion_1, futbolista.genero);

  return (
    <div
      className={`futbolista-card ${ageColorClass}`}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
      title={`Ver ProPoints de ${futbolista.nombre}`}
    >
      {/* Fondo con imagen según posición */}
      <div className="card-background">
        {backgroundImage ? (
          <img
            src={backgroundImage}
            alt={backgroundImage.includes('portero') ? 'Portero' : 'Jugador de campo'}
            className="position-background"
          />
        ) : (
          <img
            src="/img/logos/liga-prokids-logo.png"
            alt="ProKids Logo"
            className="logo-background"
          />
        )}
      </div>

      {/* Contenido de la ficha - solo imagen */}
      <div className="card-content">
        {/* Espacio completo para la imagen */}
        <div className="card-body">
          <div className="image-space"></div>
        </div>
      </div>

      {/* Información en borde inferior */}
      <div className="card-info-bar">
        <div className="futbolista-nombre-bar">
          {futbolista.nombre}
          {futbolista.apodo && futbolista.apodo !== futbolista.nombre && (
            <span className="futbolista-apodo-bar"> &ldquo;{futbolista.apodo}&rdquo;</span>
          )}
        </div>
        <div className="stats-bar">
          {futbolista.inscripciones && futbolista.inscripciones.length > 0 ? (
            futbolista.inscripciones.map(insc => {
              const abrev = insc.torneo?.nombre ? insc.torneo.nombre.split(' ').map(w => w[0]).join('') : '';
              const pp = insc.nivelAsignado ? Math.round(Number(insc.nivelAsignado.propoints_acumulados) || 0) : null;
              return (
                <div key={insc.id} className="xp-display-bar" title={insc.torneo?.nombre || 'Torneo'}>
                  ⭐{pp !== null ? pp : (temporadaActual?.puntos_propoint || 0)} ⚽{insc.saldo_jornadas || 0}/{insc.saldo_comprado || 0} {abrev}
                </div>
              );
            })
          ) : (
            <div className="xp-display-bar">
              ⭐ {temporadaActual?.puntos_propoint || 0} ⚽ 0/0
            </div>
          )}
        </div>
      </div>

      {/* Botón de renovar temporada */}
      {necesitaRenovar && onRenovar && (
        <div
          className="renovar-overlay"
          onClick={(e) => {
            e.stopPropagation();
            onRenovar(futbolista);
          }}
          title={`Renovar para ${temporadaActivaSistema.nombre}`}
        >
          <FaSyncAlt style={{ marginRight: '4px' }} />
          Renovar temporada
        </div>
      )}

      {/* Decoración adicional */}
      <div className="card-decoration">
        <div className="corner-decoration top-left"></div>
        <div className="corner-decoration top-right"></div>
        <div className="corner-decoration bottom-left"></div>
        <div className="corner-decoration bottom-right"></div>
      </div>
    </div>
  );
};

export default FutbolistaCard;
